import { BillableSourceType, useGetBillableConfig, useUpsertBillableConfig } from 'api/billableConfigs';
import { updateQuestion } from 'redux/actions/QuestionBuilderActions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';


const useBillable = ({
  crId,
  crqId,
  enabled,
  isEditMode,
}) => {
  const dispatch = useDispatch();
  const [isBillable, setIsBillable] = useState(false);
  const [originalBillableValue, setOriginalBillableValue] = useState(false);

  const { pendingCrqBillable } = useSelector(state => state.questionBuilder.question);


  const {
    data: cleanRoomBillableConfig,
    isSuccess: isCrSuccess,
    isFetching: isCrFetching,
  } = useGetBillableConfig(
    crId,
    BillableSourceType.BILLABLE_CR,
    enabled && !!crId,
  );

  const {
    data: crqBillableConfig,
    isFetching: isBFetching,
    isSuccess,
  } = useGetBillableConfig(
    crqId,
    BillableSourceType.BILLABLE_CRQ,
    enabled && !!crqId && isEditMode, // Only fetch in edit mode when crqId exists
  );

  const {
    mutate: upsertCrqBillableConfig,
    isLoading: isBCreating,
    isSuccess: isBUpserted,
  } = useUpsertBillableConfig();

  const saveBillable = () => {
    // Create mode: Store in Redux for later (like template questions)
    if (!isEditMode) {
      const billableValue = isBillable ? 'true' : 'false';
      dispatch(updateQuestion({
        pendingCrqBillable: billableValue,
      }));

      setOriginalBillableValue(isBillable);
      return;
    }

    const payload = {
      sourceID: crqId,
      sourceType: BillableSourceType.BILLABLE_CRQ,
      value: isBillable.toString(),
    };

    // Include ID if updating existing config
    if (crqBillableConfig?.ID) {
      payload.ID = crqBillableConfig.ID;
    }

    upsertCrqBillableConfig(payload);
  };


  // Determine billable value based on configs
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let value = false;

    if (isEditMode) {
      // Edit mode: Need CRQ config to be loaded
      if (!isSuccess) {
        return;
      }

      if (crqBillableConfig) {
        // 1. CRQ-specific config exists (highest priority)
        value = crqBillableConfig.value === 'true';
      }
      else if (isCrSuccess && cleanRoomBillableConfig) {
        // 2. No CRQ config, but cleanroom config exists (inherit from cleanroom)
        value = cleanRoomBillableConfig.value === 'true';
      }
    }
    else {
      let originalValue = false;
      if (isCrSuccess && cleanRoomBillableConfig) {
        originalValue = cleanRoomBillableConfig.value === 'true';

        dispatch(updateQuestion({
          cleanRoomBillableConfig: cleanRoomBillableConfig.value,
        }));
      }

      if (pendingCrqBillable !== undefined) {
        // Use the pending value from Redux if available (user has changed it)
        value = pendingCrqBillable === 'true' || pendingCrqBillable === true;
      }
      else if (isCrSuccess && cleanRoomBillableConfig) {
        // In create mode, inherit from cleanroom if no pending value
        value = originalValue;
        const billableValue = value ? 'true' : 'false';
        dispatch(updateQuestion({
          pendingCrqBillable: billableValue,
        }));
      }
    }

    setIsBillable(value);
    setOriginalBillableValue(value);
  }, [enabled, isEditMode, isSuccess, pendingCrqBillable,
    isCrSuccess, crqBillableConfig, cleanRoomBillableConfig, dispatch, crId, crqId]);

  const resetBillable = () => {
    // Reset to the original value
    setIsBillable(originalBillableValue);
  };


  return {
    isBFetching: isBFetching || isCrFetching,
    isBCreating,
    isBUpdating: isBCreating,
    isBUpserted,
    isBillable,
    setIsBillable,
    saveBillable,
    resetBillable,
  };
};

export default useBillable;
